import BlogPostAPI from "@api/BlogPost.api";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Container, Divider, Image, Stack, Text } from "@mantine/core";
import AppSkeleton from "@modules/loader/AppSkeleton";
import { useLoaderStore } from "@modules/loader/useLoader.hook";
import SEO from "@modules/seo/SEO";
import { useMemo } from "react";
import { useParams } from "react-router";
import RecentBlogs from "../components/RecentBlogs";
import BlogBody from "../components/blog/BlogContent";
import BlogHeader from "../components/blog/BlogHeader";
import BlogFooter from "../components/widgets/BlogFooter";
import useBlogStore, { useBlog } from "../stores/Blog.store";

const Blog: React.FC = () => {
  const { slug } = useParams();

  const blogStore = useBlogStore();
  const storedBlog = useBlog(slug);
  const { t } = useAppTranslation("pages.blog");

  const blog = useMemo(() => {
    if (storedBlog) {
      return storedBlog;
    }

    if (!slug) {
      return;
    }

    BlogPostAPI.getBlogPostBySlug(slug).then((res) => {
      blogStore.setBlogs([...useBlogStore.getState().blogs, res.data]);
    });
  }, [slug, storedBlog, blogStore]);

  const isLoading = useLoaderStore().isLoadingBySkeleton();

  return (
    <div>
      <SEO
        description={blog?.metadata?.description ?? t("seo.description")}
        title={blog?.metadata?.title ?? t("seo.title")}
      />
      <div
        className="darker-gradient-image-[var(--image-url)] -z-10 absolute top-0 left-0 h-1/6 w-full bg-cover bg-top bg-no-repeat sm:h-1/4 lg:h-1/3"
        style={
          {
            "--image-url": `url(${blog?.coverImage})`,
          } as React.CSSProperties
        }
      />
      <Container className="px-0 xs:px-2 pt-10 xs:pt-32" size={1000}>
        <Stack align="start">
          <BlogHeader blog={blog} />
          <Divider
            className="mt-2 border-primary-lighter pb-3"
            classNames={{ root: "w-full" }}
          />
          <Image
            className="rounded-xl border-[1px] border-primary-lighter"
            height={570}
            mah={570}
            src={blog?.coverImage}
            width={970}
          />
          {/* This is only when loading the page to display some fake lines */}
          {isLoading && (
            <Stack className="w-full">
              {Array(30)
                .fill(0)
                .map((_, i) => (
                  <AppSkeleton key={i} className="!w-full">
                    <Text>secret smurf comptes lol niveau 30</Text>
                  </AppSkeleton>
                ))}
            </Stack>
          )}

          <BlogBody blog={blog} />

          <BlogFooter blog={blog} />
        </Stack>
      </Container>

      <Stack>
        <Divider
          className="mb-10 border-primary-lighter"
          classNames={{ root: "w-full" }}
        />
        <SSHeadline
          props={{
            stack: {
              align: "left",
            },
            title: {
              order: 3,
            },
          }}
        >
          <SSLoLGradientText>
            {t("recentArticles.titlePrefix")}
          </SSLoLGradientText>
          &nbsp;
          <SSWhiteText>{t("recentArticles.titleSuffix")}</SSWhiteText>
        </SSHeadline>
        <RecentBlogs blogIdToExclude={blog?.id} />
      </Stack>
    </div>
  );
};

export default Blog;
